import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingDown, Ruler, Clock, Utensils, Dumbbell, HeartHandshake, ChevronDown, ShieldCheck, MapPin, Quote } from 'lucide-react';
import { Seo, breadcrumbJsonLd, faqJsonLd } from '@/components/Seo';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { BeforeAfterCompare } from '@/components/BeforeAfterCompare';
import { LeadForm } from '@/components/LeadForm';
import { SITE, TRANSFORMATIONS } from '@/data/site-config';

// SEO / AI-answer-engine friendly FAQ block.
// These questions target real search intent around Stuart/Martin County weight-loss
// personal-training, and are designed to be picked up by Google FAQ rich results and
// AI answer engines (ChatGPT, Perplexity, Gemini) as concise, factual sources.
const TRANSFORMATION_FAQS = [
  {
    q: `How long does it take to see real weight loss results with a personal trainer in ${SITE.city}, ${SITE.stateAbbr}?`,
    a: `Most clients start to notice changes in energy, sleep, and how their clothes fit within the first 4–8 weeks of consistent private personal training. Meaningful weight-loss results usually show up between 3 and 9 months. Terry Longo, a client of ${SITE.owner} at ${SITE.name}, lost 40 lbs and 6 inches off her waist in 7 months of consistent 1-on-1 training combined with a nutrition change.`,
  },
  {
    q: `How much weight can you realistically lose in 6 months with a personal trainer?`,
    a: `A safe, sustainable rate of loss is roughly 1–2 lbs per week when nutrition and training are aligned — which translates to 25–50 lbs over 6 months for many people. Individual results depend on starting point, consistency, nutrition, sleep, stress, and medical factors. At ${SITE.name} in ${SITE.city}, ${SITE.stateAbbr}, we build plans around the individual, not a one-size-fits-all program.`,
  },
  {
    q: `Is private personal training in Stuart, FL worth it for weight loss?`,
    a: `Private personal training is the fastest way to get real accountability, correct form, and a program built for your body — three things that most people who struggle with weight loss are missing. In our private ${SITE.city} studio there are no crowds, no waiting for equipment, and your entire session is coached by a NASM-certified trainer. That environment is why most of our clients stick with it long enough to see actual results.`,
  },
  {
    q: `What does a client transformation program at ${SITE.name} look like?`,
    a: `Every program starts with a free consultation so we understand your goals, schedule, injury history, and starting point. From there, we build a 1-on-1 or semi-private training plan (typically 2–4 sessions per week) and layer in habit-based nutrition coaching. You train inside our private appointment-only studio at ${SITE.address.line1} in ${SITE.city}, ${SITE.stateAbbr}, with weekly check-ins, progress measurements, and program adjustments.`,
  },
  {
    q: `Do you work with women over 40, 50, or 60 for weight loss and strength?`,
    a: `Yes — a large portion of our client base is women over 40 who want to lose weight, get stronger, and feel more confident. Terry Longo is one of many. Programs are always scaled to your current fitness level, joint health, and lifestyle. The goal is real results without pain, injury, or burnout.`,
  },
  {
    q: `Where is ${SITE.name} located and what areas do you serve?`,
    a: `Our private studio is at ${SITE.address.full}. We serve clients across ${SITE.county}, ${SITE.state} — including Stuart, Palm City, Jensen Beach, Hobe Sound, Port Salerno, Sewall's Point, Rio, Indiantown, and Ocean Breeze. Sessions are by appointment.`,
  },
  {
    q: `Are the before-and-after photos on this page real clients?`,
    a: `Yes. Every transformation shown on this page is a real client of ${SITE.name}, published only with that client’s written consent. Numbers (weight lost, inches lost, timeframe) are self-reported by the client and verified by their coach ${SITE.owner}. We do not use stock photos or AI-generated images.`,
  },
];

// Google Review schema for the featured client story.
const reviewJsonLd = (t) => ({
  '@context': 'https://schema.org',
  '@type': 'Review',
  itemReviewed: {
    '@type': 'LocalBusiness',
    name: SITE.name,
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE.address.line1,
      addressLocality: SITE.city,
      addressRegion: SITE.stateAbbr,
      postalCode: '34997',
      addressCountry: 'US',
    },
  },
  reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
  author: { '@type': 'Person', name: t.clientName },
  reviewBody: t.coachSummary,
});

export default function TransformationsPage() {
  const path = '/client-transformations';
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Client Transformations', path },
  ];
  const featured = TRANSFORMATIONS[0];

  const jsonLd = [
    breadcrumbJsonLd(breadcrumbs),
    faqJsonLd(TRANSFORMATION_FAQS),
    reviewJsonLd(featured),
  ];

  return (
    <>
      <Seo
        title={`Client Transformations — Real Weight Loss & Strength Results in ${SITE.city}, ${SITE.stateAbbr}`}
        description={`Real before-and-after transformations from clients of ${SITE.owner}, NASM-certified personal trainer at ${SITE.name} in ${SITE.city}, ${SITE.stateAbbr}. Weight loss, strength, and confidence built through private personal training in ${SITE.county}. Featuring Terry Longo — 40 lbs and 6 inches off the waist in 7 months.`}
        canonical={path}
        image={`${SITE.publicUrl}${featured.afterImage.src}`}
        jsonLd={jsonLd}
      />
      <Breadcrumbs items={breadcrumbs} />

      {/* HERO */}
      <section className="container-x py-10" data-testid="transformations-hero">
        <span className="eyebrow">Real clients · Real results</span>
        <h1 className="font-display text-4xl md:text-5xl font-bold mt-3" data-testid="transformations-h1">
          Client transformations at {SITE.name}.
        </h1>
        <p className="text-[color:var(--ft-muted)] text-lg mt-4 max-w-3xl">
          Real before-and-after stories from clients coached by {SITE.owner}, NASM-certified personal trainer in {SITE.city}, {SITE.stateAbbr}. Every photo, every number, every quote is verified — no stock imagery, no fabricated results. Published only with the client’s written consent.
        </p>
      </section>

      {/* FEATURED TRANSFORMATION — Terry */}
      <section className="container-x pb-4" data-testid="transformation-featured">
        <div className="grid gap-8 lg:grid-cols-12 items-start">
          {/* Left: before/after compare */}
          <div className="lg:col-span-6">
            <BeforeAfterCompare
              before={featured.beforeImage}
              after={featured.afterImage}
              initial={50}
              testId="transformation-featured-compare"
            />
            <p className="text-xs text-[color:var(--ft-dim)] mt-3 text-center">
              Drag the divider to compare · Published with {featured.clientName}&rsquo;s consent.
            </p>
          </div>

          {/* Right: story + metrics */}
          <div className="lg:col-span-6">
            <span className="eyebrow">Featured transformation</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3" data-testid="transformation-name">
              {featured.clientName} — {featured.metrics[0].value} down in {featured.durationLabel}.
            </h2>
            <p className="text-[color:var(--ft-muted)] mt-3 flex items-center gap-2">
              <MapPin size={14} className="text-accent" /> {featured.location} · Focus: {featured.focus}
            </p>

            {/* Metrics grid */}
            <div className="grid grid-cols-3 gap-3 mt-6" data-testid="transformation-metrics">
              {featured.metrics.map((m, i) => {
                const Icon = i === 0 ? TrendingDown : i === 1 ? Ruler : Clock;
                return (
                  <div key={m.label} className="surface p-4 text-center">
                    <Icon size={18} className="text-accent mx-auto" />
                    <div className="font-display text-2xl font-bold mt-2" data-testid={`transformation-metric-${i}`}>{m.value}</div>
                    <div className="text-xs text-[color:var(--ft-muted)] mt-1">{m.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Coach summary quote */}
            <div className="surface relative p-6 mt-6">
              <Quote size={40} className="absolute -top-1 -left-1 text-accent opacity-15" aria-hidden="true" />
              <p className="text-lg leading-relaxed text-[color:var(--ft-text)] relative">
                {featured.coachSummary}
              </p>
              <p className="text-sm text-[color:var(--ft-muted)] mt-3 relative">
                — Summary from coach {SITE.owner}
              </p>
            </div>

            <Link to="/contact" className="btn btn-primary btn-lg mt-6" data-testid="transformation-cta">
              Start your transformation <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW SHE DID IT */}
      <section className="container-x py-10" data-testid="transformation-approach">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <span className="eyebrow">The approach</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">
              How {featured.clientName.split(' ')[0]} did it.
            </h2>
            <p className="text-[color:var(--ft-muted)] mt-4">
              There’s no secret — just the same three things every one of our long-term clients does. Consistency wins.
            </p>
          </div>
          <div className="lg:col-span-8 grid gap-4 sm:grid-cols-3">
            {[
              { icon: Utensils, title: 'Nutrition overhaul', body: 'Habit-based eating changes she could actually keep — not a crash diet.' },
              { icon: Dumbbell, title: 'Strength & conditioning', body: 'Consistent private 1-on-1 sessions in our Stuart studio, programmed for her body.' },
              { icon: HeartHandshake, title: 'Weekly accountability', body: 'Regular check-ins with her coach — measurements, adjustments, and support.' },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="surface p-6">
                <Icon size={22} className="text-accent" />
                <h3 className="font-display text-lg font-semibold mt-3">{title}</h3>
                <p className="text-[color:var(--ft-muted)] mt-2 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REALISTIC EXPECTATIONS */}
      <section className="container-x py-10" data-testid="transformation-expectations">
        <div className="max-w-3xl">
          <span className="eyebrow">What to expect</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">
            Realistic timelines for real transformations.
          </h2>
          <p className="text-[color:var(--ft-muted)] text-lg mt-4">
            We don’t sell 30-day miracles. Sustainable transformations happen in phases — and knowing the timeline up front is what keeps clients consistent when quick-fix programs get abandoned.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-4 mt-6">
          {[
            { window: 'Weeks 1–4', title: 'Foundation', body: 'Movement patterns dialed in, nutrition habits started, sleep and energy improve.' },
            { window: 'Weeks 4–12', title: 'Momentum', body: 'Strength climbs, clothes start fitting differently, first noticeable weight drop.' },
            { window: 'Months 3–6', title: 'Visible change', body: 'Photos and measurements shift meaningfully. This is when confidence starts to build.' },
            { window: 'Months 6–12+', title: 'Transformation', body: 'The result you actually wanted — and now it\u2019s your normal, not a phase.' },
          ].map((s) => (
            <div key={s.title} className="surface p-5">
              <div className="text-accent font-display font-bold text-xs uppercase tracking-wider">{s.window}</div>
              <h3 className="font-display text-lg font-semibold mt-2">{s.title}</h3>
              <p className="text-[color:var(--ft-muted)] mt-2 text-sm leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ — SEO / AI answer engines */}
      <section className="container-x py-10" data-testid="transformation-faq">
        <div className="max-w-3xl">
          <span className="eyebrow">Common questions</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-6">
            Frequently asked: personal training transformations in {SITE.city}, {SITE.stateAbbr}.
          </h2>
          <div className="grid gap-2">
            {TRANSFORMATION_FAQS.map((f, i) => (
              <details key={i} className="surface p-5 group" data-testid={`transformation-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none font-semibold gap-4">
                  <span>{f.q}</span>
                  <ChevronDown size={18} className="shrink-0 transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-[color:var(--ft-muted)] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="container-x pb-6" data-testid="transformation-disclaimer">
        <div className="surface p-5 max-w-3xl border-l-4" style={{ borderLeftColor: 'var(--ft-accent)' }}>
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-accent shrink-0 mt-0.5" />
            <div>
              <h3 className="font-display font-semibold">A quick note on results.</h3>
              <p className="text-sm text-[color:var(--ft-muted)] mt-1 leading-relaxed">
                Individual results vary based on consistency, nutrition, sleep, medical history, and starting point. Transformations shown are real clients but are not guaranteed for every person. Photos and metrics are published only with the client’s written consent.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MORE COMING */}
      {TRANSFORMATIONS.length === 1 && (
        <section className="container-x py-6" data-testid="transformation-more-coming">
          <div className="surface p-6 max-w-3xl">
            <h3 className="font-display text-xl font-semibold">More client stories coming.</h3>
            <p className="text-[color:var(--ft-muted)] mt-2">
              We publish transformations only as clients give consent to share theirs. Check back — or start yours today.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to="/reviews" className="btn btn-secondary">Read verified reviews <ArrowRight size={16} /></Link>
              <Link to="/contact" className="btn btn-primary">Start your transformation <ArrowRight size={16} /></Link>
            </div>
          </div>
        </section>
      )}

      {/* LEAD FORM */}
      <section className="container-x py-10" data-testid="transformation-lead">
        <div className="grid gap-8 lg:grid-cols-12 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow">Ready to start?</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">
              Your transformation starts with one conversation.
            </h2>
            <p className="text-[color:var(--ft-muted)] mt-4 text-lg">
              Book a free consultation with {SITE.owner} at our private {SITE.city} studio. We&rsquo;ll talk through your goal, your starting point, and whether we&rsquo;re the right fit — no pressure, no obligation.
            </p>
          </div>
          <div className="lg:col-span-7">
            <LeadForm source={path} testIdPrefix="transformation-lead" />
          </div>
        </div>
      </section>
    </>
  );
}
